import { Tabs } from 'antd';
import { FileX, ShieldAlert } from 'lucide-react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { DetailPageSkeleton } from '../../../shared/components/DetailPageSkeleton';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageHeader } from '../../../shared/components/PageHeader';
import { withinSection } from '../../../shared/components/section-trail';
import { AccessorialRatesTab } from '../features/accessorial-rates/components/AccessorialRatesTab';
import { rateCardName } from '../components/build-rate-card-columns';
import { DeactivateRateCardButton } from '../components/DeactivateRateCardButton';
import { LinesTab } from '../features/lines/components/LinesTab';
import { ProposalsTab } from '../features/proposals/components/ProposalsTab';
import { RATE_CARD_TAB_LABELS, RATE_CARD_TABS, rateCardTabPath, type RateCardTab } from '../rate-cards.paths';
import { useRateCardPermissions } from '../hooks/use-rate-card-permissions';
import { useRateCardRecord } from '../hooks/use-rate-card-record';

/** A rate card's own page: its lines, its accessorial rates and the proposals waiting on a decision, each tab its own route. */
export function RateCardDetailPage({ tab }: { tab: RateCardTab }) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { state, reload } = useRateCardRecord(id);
  const { canEdit } = useRateCardPermissions();

  if (state.status === 'forbidden') return <Navigate to="/" replace />;
  if (state.status === 'loading')
    return <DetailPageSkeleton label="Loading rate card" breadcrumbs={withinSection('rate-cards')} tabs={RATE_CARD_TABS.length} fields={4} />;
  if (state.status === 'not_found')
    return <EmptyState icon={FileX} title="Rate card not found" description="This rate card doesn't exist, or the link is out of date." />;
  if (state.status === 'error') {
    return (
      <EmptyState icon={ShieldAlert} title="Couldn't load this rate card" description="Something went wrong loading this rate card. Try reloading the page." />
    );
  }

  const { card } = state;

  return (
    <>
      <PageHeader
        breadcrumbs={withinSection('rate-cards')}
        title={rateCardName(card)}
        description={card.isActive ? undefined : 'Deactivated: no new lines can be added.'}
        actions={canEdit && <DeactivateRateCardButton card={card} onDeactivated={reload} />}
      />
      <Tabs
        activeKey={tab}
        destroyOnHidden
        onChange={(key) => navigate(rateCardTabPath(card.id, key as RateCardTab))}
        items={[
          { key: 'lines', label: RATE_CARD_TAB_LABELS.lines, children: <LinesTab card={card} /> },
          { key: 'accessorial-rates', label: RATE_CARD_TAB_LABELS['accessorial-rates'], children: <AccessorialRatesTab card={card} /> },
          { key: 'proposals', label: RATE_CARD_TAB_LABELS.proposals, children: <ProposalsTab card={card} /> },
        ]}
      />
    </>
  );
}
